import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Badge, Spinner, Row, Col, Card, CardBody, CardTitle, CardText,
  Input, Label, Button, Table, Form,
} from 'reactstrap';
import { PageHeader } from '../components/PageHeader';
import { OverviewGrid } from '../components/OverviewGrid';
import { eventsFetch, isEventsProxyEnabled } from '../events/eventsClient';
import { ApiProxyGuard } from '../components/ApiProxyGuard';
import { ApiHealthStrip } from '../components/ApiHealthStrip';

/**
 * Events API — erstes public Scraping-Produkt (Katalog von Veranstaltungen,
 * gescrapt via Probe). Diese Seiten steuern Quellen, Zeitpläne, Test-Scrapes
 * und den Event-Katalog — verdrahtet gegen /proxy/events (events-api).
 */
// #116: delegiert an den gemeinsamen ApiProxyGuard (statt lokaler Kopie).
function ProxyGuard({ children }) {
  return (
    <ApiProxyGuard enabled={isEventsProxyEnabled()} label="Events" envVar="EVENTS_API_URL" proxyPath="/proxy/events">
      {children}
    </ApiProxyGuard>
  );
}

const CARDS = [
  { to: '/admin/apis/events/sources', icon: '🎯', title: 'Quellen & Schedule', status: 'live',
    description: 'Scraping-Quellen (Rezepte) verwalten, Zeitpläne setzen, sofort scrapen.' },
  { to: '/admin/apis/events/test', icon: '🧪', title: 'Seite testen', status: 'live',
    description: 'Eine Kandidaten-Seite + Rezept ad-hoc scrapen (Dry-Run) — vor dem Anlegen.' },
  { to: '/admin/apis/events/catalog', icon: '📅', title: 'Event-Katalog', status: 'live',
    description: 'Normalisierte Events durchsuchen (Standort / Art / Zeitraum).' },
];

// ── Übersicht ───────────────────────────────────────────────────────────
export function EventsOverviewPage() {
  const health = useQuery({ queryKey: ['events-health'], queryFn: () => eventsFetch('/healthz'), refetchInterval: 30_000, retry: false, enabled: isEventsProxyEnabled() });
  const stats = useQuery({ queryKey: ['events-stats'], queryFn: () => eventsFetch('/v1/events/stats'), retry: false, enabled: isEventsProxyEnabled() });
  const sources = useQuery({ queryKey: ['events-sources'], queryFn: () => eventsFetch('/v1/sources'), retry: false, enabled: isEventsProxyEnabled() });
  const enrich = useQuery({ queryKey: ['events-enrichment'], queryFn: () => eventsFetch('/v1/events/enrichment'), retry: false, refetchInterval: 20_000, enabled: isEventsProxyEnabled() });
  const srcItems = sources.data?.items || [];
  const lt = enrich.data?.libretranslate;
  const ol = enrich.data?.ollama;
  const geo = enrich.data?.geocode;

  return (
    <div>
      <PageHeader title="Events API · Übersicht"
        subtitle="Veranstaltungs-Katalog, gescrapt via Probe. Quellen + Schedule + Katalog." />
      <ProxyGuard>
        <ApiHealthStrip health={health} />
        <Row className="g-3 mb-3">
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Quellen</CardTitle>
            <CardText className="fs-4">{srcItems.length}</CardText>
            <div className="small text-muted">{srcItems.filter((s) => s.enabled).length} aktiv · {srcItems.filter((s) => s.schedule_cron).length} geplant</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Events im Katalog</CardTitle>
            <CardText className="fs-4">{stats.data?.total ?? '–'}</CardText>
            <div className="small text-muted">{stats.data?.byCity?.length ?? 0} Städte</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Nach Land</CardTitle>
            <CardText className="fs-4">{stats.data?.byCountry?.length ?? '–'}</CardText>
            <div className="small">{(stats.data?.byCountry || []).slice(0, 6).map((c) => `${c.country || '?'}: ${c.n}`).join(' · ') || '–'}</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Nach Stadt</CardTitle>
            <div className="small">{(stats.data?.byCity || []).slice(0, 5).map((c) => `${c.city}: ${c.n}`).join(' · ') || '–'}</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Nach Kategorie</CardTitle>
            <div className="small">{(stats.data?.byCategory || []).slice(0, 5).map((c) => `${c.category}: ${c.n}`).join(' · ') || '–'}</div>
            {stats.data?.free != null && <div className="small text-muted mt-1">{stats.data.free} kostenlos</div>}
          </CardBody></Card></Col>
        </Row>

        {/* Hintergrund-Anreicherung: Übersetzungen (LibreTranslate) + Ollama + Geocoding */}
        <Row className="g-3 mb-3">
          <Col md={4}><Card><CardBody>
            <CardTitle tag="h6">LibreTranslate
              <Badge color={lt?.enabled ? 'success' : 'secondary'} pill className="ms-2">{lt?.enabled ? 'aktiv' : 'aus'}</Badge></CardTitle>
            {enrich.isLoading ? <Spinner size="sm" /> : (
              <div className="small">
                <div><Badge color="success" pill>{lt?.jobs?.done ?? 0}</Badge> fertig ·
                  {' '}<Badge color="warning" pill>{(lt?.jobs?.pending ?? 0) + (lt?.jobs?.running ?? 0)}</Badge> wartend ·
                  {' '}<Badge color="danger" pill>{lt?.jobs?.failed ?? 0}</Badge> Fehler</div>
                <div className="text-muted mt-1">{lt?.translated_events ?? 0} Events übersetzt · Sprachen: {(lt?.target_langs || []).map((l) => l.toUpperCase()).join(', ') || '–'}</div>
              </div>
            )}
          </CardBody></Card></Col>
          <Col md={4}><Card><CardBody>
            <CardTitle tag="h6">Ollama (LLM)
              <Badge color={ol?.enabled ? 'success' : 'secondary'} pill className="ms-2">{ol?.enabled ? 'aktiv' : 'aus'}</Badge></CardTitle>
            <div className="small">
              <div><Badge color="info" pill>{ol?.descriptions_generated ?? 0}</Badge> Beschreibungen generiert</div>
              <div className="text-muted mt-1">Modell: {ol?.model || '–'} · enrich: {ol?.enrich_translations ? 'an' : 'aus'} · generate: {ol?.generate_descriptions ? 'an' : 'aus'}</div>
            </div>
          </CardBody></Card></Col>
          <Col md={4}><Card><CardBody>
            <CardTitle tag="h6">Geocoding
              <Badge color={geo?.enabled ? 'success' : 'secondary'} pill className="ms-2">{geo?.enabled ? 'aktiv' : 'aus'}</Badge></CardTitle>
            <div className="small">
              <div><Badge color="success" pill>{geo?.geocoded ?? 0}</Badge> verortet · <Badge color="warning" pill>{geo?.pending ?? 0}</Badge> offen</div>
              <div className="text-muted mt-1">Land + Viertel via Nominatim</div>
            </div>
          </CardBody></Card></Col>
        </Row>
        <OverviewGrid cards={CARDS} />
      </ProxyGuard>
    </div>
  );
}

// ── Quellen & Schedule ──────────────────────────────────────────────────
export function EventsSourcesPage() {
  const qc = useQueryClient();
  const sources = useQuery({ queryKey: ['events-sources'], queryFn: () => eventsFetch('/v1/sources'), refetchInterval: 15_000, retry: false, enabled: isEventsProxyEnabled() });
  const [form, setForm] = useState({ slug: '', name: '', city: '', list_url: '', schedule_cron: '', recipe: '' });

  const save = useMutation({
    mutationFn: (body) => eventsFetch('/v1/sources', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events-sources'] }),
  });
  const scrape = useMutation({
    mutationFn: (slug) => eventsFetch(`/v1/sources/${slug}/scrape`, { method: 'POST' }),
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['events-sources'] }), 1500),
  });

  function submit(e) {
    e.preventDefault();
    let recipe = {};
    try { recipe = form.recipe ? JSON.parse(form.recipe) : {}; } catch { save.reset(); alert('Rezept ist kein gültiges JSON'); return; }
    save.mutate({
      slug: form.slug, name: form.name, city: form.city || undefined,
      list_url: form.list_url, schedule_cron: form.schedule_cron || undefined, recipe,
    });
  }

  return (
    <div>
      <PageHeader title="Events · Quellen & Schedule" subtitle="Scraping-Quellen + Rezepte + cron-Zeitpläne." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <CardTitle tag="h6">Quellen</CardTitle>
          {sources.isLoading ? <Spinner size="sm" />
            : !sources.data?.items?.length ? <CardText className="small text-muted">Noch keine Quellen.</CardText>
            : (
              <Table size="sm" responsive hover>
                <thead><tr><th>Slug</th><th>Stadt</th><th>Cron</th><th>Letzter Lauf</th><th>Status</th><th>Events</th><th></th></tr></thead>
                <tbody>
                  {sources.data.items.map((s) => (
                    <tr key={s.slug}>
                      <td><code>{s.slug}</code>{!s.enabled && <Badge color="secondary" pill className="ms-1">aus</Badge>}</td>
                      <td>{s.city || '–'}</td>
                      <td className="small">{s.schedule_cron || '—'}</td>
                      <td className="small text-muted">{s.last_run_at ? new Date(s.last_run_at).toLocaleString() : '—'}</td>
                      <td><Badge color={s.last_status === 'completed' ? 'success' : s.last_status === 'failed' ? 'danger' : 'secondary'} pill>{s.last_status || '—'}</Badge></td>
                      <td>{s.last_event_count ?? '–'}</td>
                      <td><Button size="sm" color="link" className="p-0" disabled={scrape.isPending} onClick={() => scrape.mutate(s.slug)}>scrape</Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          {scrape.isError && <Alert color="danger" className="small mt-2">{scrape.error?.message}</Alert>}
        </CardBody></Card>

        <Card><CardBody>
          <CardTitle tag="h6">Quelle anlegen / aktualisieren</CardTitle>
          <Form onSubmit={submit}>
            <Row className="g-2">
              <Col md={3}><Label className="small">Slug</Label><Input bsSize="sm" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="aachen-stadt" /></Col>
              <Col md={4}><Label className="small">Name</Label><Input bsSize="sm" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Col>
              <Col md={2}><Label className="small">Stadt</Label><Input bsSize="sm" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Col>
              <Col md={3}><Label className="small">Cron</Label><Input bsSize="sm" value={form.schedule_cron} onChange={(e) => setForm({ ...form, schedule_cron: e.target.value })} placeholder="0 6 * * *" /></Col>
              <Col md={12}><Label className="small">Listing-URL</Label><Input bsSize="sm" required value={form.list_url} onChange={(e) => setForm({ ...form, list_url: e.target.value })} /></Col>
              <Col md={12}><Label className="small">Rezept (JSON: container, fields, wait_ms, …)</Label>
                <Input type="textarea" bsSize="sm" rows={5} style={{ fontFamily: 'monospace' }} value={form.recipe} onChange={(e) => setForm({ ...form, recipe: e.target.value })}
                  placeholder='{"container":".card","fields":{"title":"h3","date_text":".date"}}' /></Col>
              <Col md={12}><Button size="sm" color="primary" type="submit" disabled={save.isPending}>Speichern</Button>
                {save.isError && <Alert color="danger" className="small mt-2">{save.error?.message}</Alert>}
                {save.isSuccess && <Alert color="success" className="small mt-2">Gespeichert.</Alert>}
              </Col>
            </Row>
          </Form>
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Seite testen (Dry-Run) ──────────────────────────────────────────────
export function EventsTestPage() {
  const [listUrl, setListUrl] = useState('');
  const [recipe, setRecipe] = useState('');
  const test = useMutation({
    mutationFn: (body) => eventsFetch('/v1/sources/test', { method: 'POST', body }),
  });
  function run(e) {
    e.preventDefault();
    let r = {};
    try { r = recipe ? JSON.parse(recipe) : {}; } catch { alert('Rezept ist kein gültiges JSON'); return; }
    test.mutate({ list_url: listUrl, recipe: r });
  }
  const rows = test.data?.sample || [];
  return (
    <div>
      <PageHeader title="Events · Seite testen" subtitle="Ad-hoc-Scrape (Dry-Run) einer Kandidaten-Seite — nichts wird gespeichert." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <Form onSubmit={run}>
            <Label className="small">Listing-URL</Label>
            <Input bsSize="sm" className="mb-2" required value={listUrl} onChange={(e) => setListUrl(e.target.value)} placeholder="https://…" />
            <Label className="small">Rezept (JSON)</Label>
            <Input type="textarea" bsSize="sm" rows={6} style={{ fontFamily: 'monospace' }} className="mb-2" value={recipe} onChange={(e) => setRecipe(e.target.value)}
              placeholder='{"container":".tvm-card__event-event","fields":{"title":".tvm-event--name","date_text":".tvm-event--date"}}' />
            <Button size="sm" color="primary" type="submit" disabled={test.isPending}>{test.isPending ? <><Spinner size="sm" /> scrape läuft …</> : 'Testen'}</Button>
          </Form>
          {test.isError && <Alert color="danger" className="small mt-2">{test.error?.message}</Alert>}
        </CardBody></Card>

        {test.data && (
          <Card><CardBody>
            <CardTitle tag="h6">Ergebnis <Badge color={test.data.status === 'completed' ? 'success' : 'warning'} pill>{test.data.status}</Badge>
              <span className="text-muted small ms-2">{test.data.extracted} Zeilen extrahiert (Sample: {rows.length})</span></CardTitle>
            {rows.length ? (
              <Table size="sm" responsive className="small">
                <thead><tr>{Object.keys(rows[0]).map((k) => <th key={k}>{k}</th>)}</tr></thead>
                <tbody>{rows.map((r, i) => (
                  <tr key={i}>{Object.keys(rows[0]).map((k) => <td key={k}>{String(r[k] ?? '').slice(0, 70)}</td>)}</tr>
                ))}</tbody>
              </Table>
            ) : <CardText className="small text-muted">Keine Zeilen — Container-Selektor prüfen.</CardText>}
          </CardBody></Card>
        )}
      </ProxyGuard>
    </div>
  );
}

// Preis-Anzeige aus den geparsten Feldern.
function formatPrice(e) {
  if (e.is_free) return 'Kostenlos';
  const min = e.price_min, max = e.price_max;
  if (min == null && max == null) return '–';
  const eur = (n) => `${Number(n) % 1 === 0 ? Number(n) : Number(n).toFixed(2)} €`;
  if (min != null && max == null) return `ab ${eur(min)}`;
  if (min != null && max != null && Number(min) === Number(max)) return eur(min);
  return `${eur(min)}–${eur(max)}`;
}

// ── Katalog ─────────────────────────────────────────────────────────────
export function EventsCatalogPage() {
  const [filters, setFilters] = useState({ city: '', district: '', country: '', q: '', category: '', lang: '', upcoming: false, free: false });
  const params = new URLSearchParams();
  if (filters.city) params.set('city', filters.city);
  if (filters.district) params.set('district', filters.district);
  if (filters.country) params.set('country', filters.country);
  if (filters.q) params.set('q', filters.q);
  if (filters.category) params.set('category', filters.category);
  if (filters.lang) params.set('lang', filters.lang);
  if (filters.upcoming) params.set('upcoming', 'true');
  if (filters.free) params.set('free', 'true');
  params.set('limit', '100');

  const events = useQuery({
    queryKey: ['events-catalog', filters],
    queryFn: () => eventsFetch(`/v1/events?${params.toString()}`),
    retry: false, enabled: isEventsProxyEnabled(),
  });
  const stats = useQuery({ queryKey: ['events-stats'], queryFn: () => eventsFetch('/v1/events/stats'), retry: false, enabled: isEventsProxyEnabled() });
  const items = events.data?.items || [];
  const cats = stats.data?.byCategory || [];
  const langs = stats.data?.target_langs || [];
  const countries = stats.data?.byCountry || [];

  return (
    <div>
      <PageHeader title="Events · Katalog" subtitle="Normalisierter Event-Katalog mit Filtern." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <Row className="g-2 align-items-end">
            <Col md={2}><Label className="small">Stadt</Label><Input bsSize="sm" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} /></Col>
            <Col md={2}><Label className="small">Viertel</Label><Input bsSize="sm" value={filters.district} onChange={(e) => setFilters({ ...filters, district: e.target.value })} placeholder="Kreuzberg" /></Col>
            <Col md={2}><Label className="small">Land</Label>
              <Input type="select" bsSize="sm" value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })}>
                <option value="">alle</option>
                {countries.map((c) => <option key={c.country || '?'} value={c.country || ''}>{(c.country || '?')} ({c.n})</option>)}
              </Input>
            </Col>
            <Col md={2}><Label className="small">Titelsuche</Label><Input bsSize="sm" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} /></Col>
            <Col md={2}><Label className="small">Kategorie</Label>
              <Input type="select" bsSize="sm" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
                <option value="">alle</option>
                {cats.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.n})</option>)}
              </Input>
            </Col>
            <Col md={2}><Label className="small">Sprache</Label>
              <Input type="select" bsSize="sm" value={filters.lang} onChange={(e) => setFilters({ ...filters, lang: e.target.value })}>
                <option value="">Original</option>
                {langs.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
              </Input>
            </Col>
            <Col md={2} className="d-flex flex-column gap-1">
              <Label check className="small"><Input type="checkbox" checked={filters.upcoming} onChange={(e) => setFilters({ ...filters, upcoming: e.target.checked })} /> nur kommende</Label>
              <Label check className="small"><Input type="checkbox" checked={filters.free} onChange={(e) => setFilters({ ...filters, free: e.target.checked })} /> nur kostenlos</Label>
            </Col>
          </Row>
        </CardBody></Card>
        <Card><CardBody>
          <CardTitle tag="h6">Events <Badge color="secondary" pill>{items.length}</Badge>
            {filters.lang && <Badge color="info" pill className="ms-2">Sprache: {filters.lang.toUpperCase()}</Badge>}</CardTitle>
          {events.isLoading ? <Spinner size="sm" />
            : !items.length ? <CardText className="small text-muted">Keine Events.</CardText>
            : (
              <Table size="sm" responsive hover>
                <thead><tr><th>Titel</th><th>Kategorie</th><th>Beginn</th><th>Ort</th><th>Viertel</th><th>Stadt</th><th>Land</th><th>Preis</th><th>Tickets</th><th>Infos</th><th>Quelle</th></tr></thead>
                <tbody>{items.map((e) => (
                  <tr key={e.id}>
                    <td>{e.url ? <a href={e.url} target="_blank" rel="noreferrer">{e.title}</a> : e.title}
                      {e.translated && <Badge color="info" pill className="ms-1" title="übersetzt">↺</Badge>}</td>
                    <td className="small">
                      {e.category ? <Badge color="light" className="text-dark border">{e.category}</Badge> : '–'}
                      {e.subcategory && <span className="text-muted ms-1">{e.subcategory}</span>}
                    </td>
                    <td className="small">{e.all_day ? (e.date_text || '—') : (e.start_at ? new Date(e.start_at).toLocaleString() : '—')}</td>
                    <td className="small">{e.venue_name || '–'}</td>
                    <td className="small">{e.district || '–'}</td>
                    <td>{e.city || '–'}</td>
                    <td className="small text-muted">{e.country || '–'}</td>
                    <td className="small">{e.is_free ? <Badge color="success" pill>Kostenlos</Badge> : formatPrice(e)}</td>
                    <td className="small">{e.ticket_url ? <a href={e.ticket_url} target="_blank" rel="noreferrer">buchen ↗</a> : '–'}</td>
                    <td className="small">{e.info_url ? <a href={e.info_url} target="_blank" rel="noreferrer">Infos ↗</a> : '–'}</td>
                    <td className="small text-muted">{e.source}</td>
                  </tr>
                ))}</tbody>
              </Table>
            )}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}
